import React, { useCallback, useMemo, useState } from 'react';
import * as d3 from 'd3';
import { ChartPane, chartContext } from '../../ui/chart/ChartPane';
import XAxis from '../../ui/chart/XAxis';
import YAxis from '../../ui/chart/YAxis';
import HorizontalGrid from '../../ui/chart/HorizontalGrid';
import Crosshairs from '../../ui/chart/Crosshairs';
import LineSeries from '../../ui/chart/LineSeries';
import { TimeRange, DataRange, ChartSeriesV2 } from '../../ui/chart/types';
import { lookupPrevious } from 'src/helpers/chart';
import { replacePercent } from 'src/helpers/strings';

import { ZoomBar } from '../../ui/chart/ZoomBar';
import { ChartTimeSeries } from './types';
import { Legend } from './ChartLegend';
import { Dictionary, difference, without } from 'lodash';
import { TimeSeries } from 'src/models';
import { makeStyles } from '@material-ui/core';
import { ChartContainer } from './ChartContainer';
import { useRanges } from './useRanges';
import { ChartsCount } from './AnalysisChart';
import { chartColors } from 'src/data/colors';

interface MainChartProps {
  height?: number;
  tagIds: string[];
  xRange: {
    start: Date;
    end: Date;
  };
  margins: { top: number; bottom: number; left: number; right: number };
  lineGroups: Dictionary<ChartSeriesV2[]>;
  zoomRange?: TimeRange;
  setZoomRange: (range: TimeRange | undefined) => void;
  fullRanges: Dictionary<DataRange>;
  isSmallScreen: boolean;
  xAxisFormatter?: (d: Date) => string;
  yAxisFormatter?: (d: number) => string;
  yLabelFormatter?: (d: number) => string;
  snapshots: Dictionary<TimeSeries>;
  removeTagFromChartGroup: (tagId: string) => void;
  editPointsHandler: () => void;
  chartsCount?: ChartsCount;
  removeChartInView: () => void;
  chartLabel: string | null;
}

const useStyles = makeStyles(() => ({
  line1: {
    stroke: chartColors[0],
  },
  line2: {
    stroke: chartColors[1],
  },
  line3: {
    stroke: chartColors[2],
  },
  line4: {
    stroke: chartColors[3],
  },
  line5: {
    stroke: chartColors[4],
  },
  line6: {
    stroke: chartColors[5],
  },
  line7: {
    stroke: chartColors[6],
  },
  line8: {
    stroke: chartColors[7],
  },
  line9: {
    stroke: chartColors[8],
  },
  line10: {
    stroke: chartColors[9],
  },
  line11: {
    stroke: chartColors[10],
  },
  line12: {
    stroke: chartColors[11],
  },
  legend1: { backgroundColor: chartColors[0] },
  legend2: { backgroundColor: chartColors[1] },
  legend3: { backgroundColor: chartColors[2] },
  legend4: { backgroundColor: chartColors[3] },
  legend5: { backgroundColor: chartColors[4] },
  legend6: { backgroundColor: chartColors[5] },
  legend7: { backgroundColor: chartColors[6] },
  legend8: { backgroundColor: chartColors[7] },
  legend9: { backgroundColor: chartColors[8] },
  legend10: { backgroundColor: chartColors[9] },
  legend11: { backgroundColor: chartColors[10] },
  legend12: { backgroundColor: chartColors[11] },
}));

export const MainChart: React.FC<MainChartProps> = ({
  snapshots,
  height = 240,
  tagIds,
  xRange,
  margins,
  lineGroups,
  zoomRange,
  setZoomRange,
  isSmallScreen,
  xAxisFormatter,
  yAxisFormatter,
  yLabelFormatter,
  removeTagFromChartGroup,
  editPointsHandler,
  chartsCount,
  removeChartInView,
  chartLabel,
}) => {
  const classes = useStyles();
  const [chartHeight, setChartHeight] = useState(height);

  const { ranges } = useRanges(tagIds, snapshots, xRange, zoomRange);

  const handleHeightChange = useCallback((newHeight: number) => {
    setChartHeight(newHeight);
  }, []);

  const lineClasses = useMemo(
    () => [
      classes.line1,
      classes.line2,
      classes.line3,
      classes.line4,
      classes.line5,
      classes.line6,
      classes.line7,
      classes.line8,
      classes.line9,
      classes.line10,
    ],
    [classes]
  );

  const legendClasses = useMemo(
    () => [
      classes.legend1,
      classes.legend2,
      classes.legend3,
      classes.legend4,
      classes.legend5,
      classes.legend6,
      classes.legend7,
      classes.legend8,
      classes.legend9,
      classes.legend10,
    ],
    [classes]
  );

  const [activeXValue, setActiveXValue] = useState<Date | undefined>(undefined);

  const [inactiveLines, setInactiveLines] = useState<string[]>([]);

  const activeLines = difference(
    Object.values(lineGroups)
      .reduce<ChartSeriesV2[]>((acc, group) => [...acc, ...group], [])
      .map((series) => series.key),
    inactiveLines
  );

  const setHighlighted = useState<string | undefined>()[1];

  const nearest = activeXValue ? lookupPrevious(snapshots, activeXValue) : undefined;

  const linesWithClasses = useMemo(() => {
    const allLines = Object.values(lineGroups).reduce((acc, group) => [...acc, ...group], []);
    return Object.entries(lineGroups).reduce((acc, [unit, group]) => {
      acc[unit] = group.map((g) => ({
        ...g,
        styleClass: lineClasses[allLines.findIndex((line) => line.key === g.key)],
      }));
      return acc;
    }, {} as Dictionary<ChartSeriesV2[]>);
  }, [lineGroups, lineClasses]);

  const orderedChartRanges = useMemo(() => {
    const units = Object.keys(linesWithClasses);
    return units.map((unit) => ranges.chart[unit]).filter((r) => r);
  }, [linesWithClasses, ranges.chart]);

  function toggleValue<E>(list: E[], value: E) {
    return list.includes(value) ? without(list, value) : [...list, value];
  }

  const chartSnapshots: Dictionary<ChartTimeSeries> = {};
  Object.values(lineGroups)
    .reduce<ChartSeriesV2[]>((acc, group) => [...acc, ...group], [])
    .forEach((series, i) => {
      chartSnapshots[series.key] = {
        ...snapshots[series.key],
        lineClass: lineClasses[i],
        legendClass: legendClasses[i],
      };
    });

  const handleMouseMove = useCallback((pos: { x: Date; y: number }) => setActiveXValue(pos.x), []);
  const handleMouseLeave = useCallback(() => setActiveXValue(undefined), []);

  if (Object.values(lineGroups).length === 0) {
    return null;
  }

  const title = chartLabel ? chartLabel : `Chart 1 of ${chartsCount?.total}`;

  return (
    <ChartContainer
      title={title}
      onHeightChange={handleHeightChange}
      allowResize
      onRemove={removeChartInView}
      minHeight={340}
      footer={
        <Legend
          snapshots={chartSnapshots}
          nearest={nearest}
          setHighlighted={(tagId) => setHighlighted(tagId)}
          toggleActive={(tagId) => setInactiveLines(toggleValue(inactiveLines, tagId))}
          active={activeLines}
          removeTagFromChartGroup={removeTagFromChartGroup}
          editPointsHandler={editPointsHandler}
        />
      }
    >
      <div style={{ height: `${chartHeight}px` }}>
        <ChartPane
          handleMouseMove={handleMouseMove}
          handleMouseLeave={handleMouseLeave}
          height={chartHeight}
          range={orderedChartRanges[0]}
          altRanges={orderedChartRanges.length > 1 ? orderedChartRanges.slice(1) : undefined}
          margins={margins}
        >
          <chartContext.Consumer>
            {(context) => {
              return (
                <>
                  <HorizontalGrid
                    size={context.size}
                    yScale={context.yScale}
                    marginLeft={context.margins.left}
                    marginRight={context.margins.right}
                    tickCount={5}
                  />
                  {Object.values(linesWithClasses).map((group, i) => {
                    const yScale =
                      i === 0 ? context.yScale : context.altYScales && context.altYScales[i - 1];
                    const activeGroups = group.filter((g) => activeLines.includes(g.key));
                    return activeGroups.length > 0 ? (
                      <LineSeries
                        key={i}
                        xScale={context.xScale}
                        size={context.size}
                        margins={context.margins}
                        yScale={yScale}
                        data={activeGroups || []}
                      />
                    ) : null;
                  })}

                  <XAxis {...context} inset={true} offset={-1} />

                  {/* Allow for multiple y axis */}
                  {Object.keys(linesWithClasses).map((unit, i) => {
                    const offset =
                      i === 0 ? context.margins.left : context.size.width - (i - 1) * 42 - 1;
                    const yScale =
                      i === 0 ? context.yScale : context.altYScales && context.altYScales[i - 1];
                    if (!yScale) {
                      return null;
                    }

                    return (
                      <YAxis
                        key={unit}
                        unit={replacePercent(unit)}
                        yScale={yScale}
                        size={context.size}
                        inset={i === 0}
                        offset={offset}
                        format={yAxisFormatter || ((d) => `${d}`)}
                      />
                    );
                  })}

                  {activeXValue && context.inRange(context.mouseX, context.mouseY) ? (
                    <Crosshairs
                      size={context.size}
                      targetPosition={{ x: context.xScale(activeXValue) ?? 0, y: context.mouseY }}
                      margins={{ ...context.margins, bottom: 0 }}
                      bottomValue={xAxisFormatter && xAxisFormatter(activeXValue)}
                      leftValue={
                        yLabelFormatter && yLabelFormatter(context.yScale.invert(context.mouseY))
                      }
                    />
                  ) : null}
                  {!isSmallScreen ? (
                    <ZoomBar
                      size={context.size}
                      xScale={(() => {
                        const scale = zoomRange
                          ? d3
                              .scaleTime()
                              .domain([zoomRange.start, zoomRange.end])
                              .range([
                                0 + (context.margins.left || 0),
                                context.size.width - (context.margins.right || 0),
                              ])
                          : context.xScale;
                        return scale;
                      })()}
                      onZoomEnd={(newZoomRange) => setZoomRange(newZoomRange)}
                    />
                  ) : null}
                </>
              );
            }}
          </chartContext.Consumer>
        </ChartPane>
      </div>
    </ChartContainer>
  );
};
